/**
 * What 3 Ingredients - Main Application Controller
 * Connects recipe engine, recipe-encoded deep linking,
 * native Web Share API, portion scaling, theme toggling,
 * unit system selection (Imperial vs Metric), cookie consent, and legal modal dialogs.
 */

import { parseIngredients } from './ingredientParser.js';
import { generateRecipe, getRandomRecipe } from './recipeEngine.js';
import { scaleQuantity } from './portionScaler.js';
import { detectUserRegion, getRegionName } from './regionDetector.js';
import { injectRecipeSchema } from './seoSchema.js';
import { triggerPrint } from './printHandler.js';
import { encodeRecipeToURL, decodeRecipeFromURL } from './urlHandler.js';
import { shareRecipe } from './shareHandler.js';
import { initCookieConsent } from './cookieConsent.js';

// Random phrase collection for loading state (zero mention of AI)
const COOKING_PHRASES = [
  "Cooking up a tasty recipe...",
  "Simmering pantry ingredients...",
  "Whisking together delicious ideas...",
  "Sautéing up your meal...",
  "Plating your 3-ingredient dish...",
  "Caramelizing your ingredients...",
  "Tossing up something fresh...",
  "Baking a quick bite..."
];

const state = {
  currentRecipe: null,
  currentServings: 2,
  selectedRegion: 'all',
  unitSystem: 'imperial',
  theme: 'dark',
  isLoading: false,
  lastGeneratedInput: '',
  seenTitles: []
};

let inputEl, clearBtn, generateBtn, randomBtn;
let portionMinusBtn, portionPlusBtn, portionCountEl, printBtn, shareBtn;
let regionSelectEl, themeToggleBtn, unitImperialBtn, unitMetricBtn;
let recipeDisplay, recipeLoadingState, loadingPhraseEl, recipeTitle, recipeTagline, recipeMetaPills;
let ingredientsList, stepsList, chefTipBox, chefTipText;
let legalOverlay, legalModalTitle, legalModalBody, closeLegalBtn;

document.addEventListener('DOMContentLoaded', () => {
  initSelectors();
  initRegion();
  initEventListeners();

  // Initialize GDPR & AdSense Cookie Consent Banner
  initCookieConsent((type) => openLegalModal(type));
  
  // Check if page loaded via a deep link URL (?r=... or ?i=...)
  checkDeepLinkBoot();
});

function initSelectors() {
  inputEl = document.getElementById('ingredient-input');
  clearBtn = document.getElementById('clear-input-btn');
  generateBtn = document.getElementById('btn-generate');
  randomBtn = document.getElementById('btn-random');
  
  unitImperialBtn = document.getElementById('unit-imperial');
  unitMetricBtn = document.getElementById('unit-metric');
  portionMinusBtn = document.getElementById('portion-minus');
  portionPlusBtn = document.getElementById('portion-plus');
  portionCountEl = document.getElementById('portion-count');
  shareBtn = document.getElementById('btn-share');
  printBtn = document.getElementById('btn-print');
  
  regionSelectEl = document.getElementById('region-select');
  themeToggleBtn = document.getElementById('theme-toggle-btn');
  
  recipeDisplay = document.getElementById('recipe-display');
  recipeLoadingState = document.getElementById('recipe-loading-state');
  loadingPhraseEl = document.getElementById('loading-phrase');
  recipeTitle = document.getElementById('recipe-title');
  recipeTagline = document.getElementById('recipe-tagline');
  recipeMetaPills = document.getElementById('recipe-meta-pills');
  
  ingredientsList = document.getElementById('ingredients-list');
  stepsList = document.getElementById('steps-list');
  chefTipBox = document.getElementById('chef-tip-box');
  chefTipText = document.getElementById('chef-tip-text');

  legalOverlay = document.getElementById('legal-modal-overlay');
  legalModalTitle = document.getElementById('legal-modal-title');
  legalModalBody = document.getElementById('legal-modal-body');
  closeLegalBtn = document.getElementById('close-legal-modal-btn');
}

function initRegion() {
  const detected = detectUserRegion();
  if (regionSelectEl) {
    regionSelectEl.value = detected;
    state.selectedRegion = detected;
  }
}

function initEventListeners() {
  inputEl.addEventListener('input', () => {
    const raw = inputEl.value;
    clearBtn.classList.toggle('hidden', !raw.trim());
    
    if (raw.trim().toLowerCase() !== state.lastGeneratedInput.trim().toLowerCase()) {
      state.seenTitles = [];
    }
    updateGenerateButtonText();
  });

  clearBtn.addEventListener('click', () => {
    inputEl.value = '';
    clearBtn.classList.add('hidden');
    state.seenTitles = [];
    updateGenerateButtonText();
    inputEl.focus();
  });

  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleGenerate();
    }
  });

  generateBtn.addEventListener('click', handleGenerate);
  randomBtn.addEventListener('click', handleRandom);

  if (unitImperialBtn && unitMetricBtn) {
    unitImperialBtn.addEventListener('click', () => setUnitSystem('imperial'));
    unitMetricBtn.addEventListener('click', () => setUnitSystem('metric'));
  }

  portionMinusBtn.addEventListener('click', () => {
    if (state.currentServings > 1) {
      state.currentServings--;
      updateServingsUI();
    }
  });
  portionPlusBtn.addEventListener('click', () => {
    if (state.currentServings < 8) {
      state.currentServings++;
      updateServingsUI();
    }
  });

  if (shareBtn) {
    shareBtn.addEventListener('click', () => {
      if (state.currentRecipe) {
        shareRecipe(state.currentRecipe, state.currentServings, showToast);
      }
    });
  }

  printBtn.addEventListener('click', triggerPrint);

  regionSelectEl.addEventListener('change', (e) => {
    state.selectedRegion = e.target.value;
    state.seenTitles = [];
    showToast(`Region: ${getRegionName(state.selectedRegion)}`);
  });

  themeToggleBtn.addEventListener('click', () => {
    state.theme = state.theme === 'light' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', state.theme);
    const themeIcon = document.getElementById('theme-icon');
    if (themeIcon) {
      themeIcon.innerHTML = state.theme === 'dark'
        ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg>'
        : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>';
    }
  });

  // Footer Region Links
  document.querySelectorAll('.footer-region-link').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const r = link.getAttribute('data-region');
      if (r && regionSelectEl) {
        regionSelectEl.value = r;
        state.selectedRegion = r;
        state.seenTitles = [];
        handleRandom();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  });

  // Legal Modal Link Listeners
  document.getElementById('open-privacy-link')?.addEventListener('click', (e) => {
    e.preventDefault();
    openLegalModal('privacy');
  });
  document.getElementById('open-cookie-link')?.addEventListener('click', (e) => {
    e.preventDefault();
    openLegalModal('cookie');
  });
  document.getElementById('open-terms-link')?.addEventListener('click', (e) => {
    e.preventDefault();
    openLegalModal('terms');
  });

  closeLegalBtn?.addEventListener('click', closeLegalModal);
  legalOverlay?.addEventListener('click', (e) => {
    if (e.target === legalOverlay) closeLegalModal();
  });
}

function setUnitSystem(unit) {
  if (state.unitSystem === unit) return;
  state.unitSystem = unit;

  if (unitImperialBtn && unitMetricBtn) {
    unitImperialBtn.classList.toggle('active', unit === 'imperial');
    unitMetricBtn.classList.toggle('active', unit === 'metric');
  }

  showToast(`Units: ${unit.toUpperCase()}`);

  if (state.currentRecipe && inputEl.value.trim()) {
    handleGenerate();
  }
}

function updateGenerateButtonText() {
  const currentVal = inputEl.value.trim().toLowerCase();
  const lastVal = state.lastGeneratedInput.trim().toLowerCase();

  if (state.currentRecipe && currentVal && currentVal === lastVal) {
    generateBtn.textContent = 'Try Again';
  } else {
    generateBtn.textContent = 'Generate Recipe';
  }
}

function openLegalModal(type) {
  if (!legalOverlay) return;

  if (type === 'privacy') {
    legalModalTitle.textContent = 'Privacy Policy';
    legalModalBody.innerHTML = `
      <h4>What Information We Collect</h4>
      <p>What 3 Ingredients does not require account creation or harvest personal contact information. We process ingredient inputs solely to generate custom recipes.</p>
      <h4>Google AdSense & Analytics</h4>
      <p>We use Google AdSense to serve non-intrusive advertisements. Google uses cookies to serve ads based on your visit to this site and other sites on the internet. Users may opt out of personalized advertising by visiting Google Ad Settings.</p>
      <h4>Data Security</h4>
      <p>No ingredient entries are permanently stored or sold to third parties.</p>
    `;
  } else if (type === 'cookie') {
    legalModalTitle.textContent = 'Cookie Policy';
    legalModalBody.innerHTML = `
      <h4>Use of Cookies</h4>
      <p>Cookies are small text files stored in your web browser. What 3 Ingredients uses essential local browser storage to remember your serving size preferences and theme settings.</p>
      <h4>Third-Party Cookies</h4>
      <p>Google AdSense and analytics partners may place cookies in your browser to measure ad performance and prevent fraud. You can clear your cookies anytime through your browser settings.</p>
    `;
  } else if (type === 'terms') {
    legalModalTitle.textContent = 'Terms of Service';
    legalModalBody.innerHTML = `
      <h4>Free Utility Service</h4>
      <p>What 3 Ingredients is provided as a free utility tool for home cooks around the world. All recipes and cooking suggestions are provided for general inspirational purposes.</p>
      <h4>Food Safety Disclaimer</h4>
      <p>Home cooks should ensure proper food safety, food handling, and safe cooking temperatures when preparing meals.</p>
    `;
  }

  legalOverlay.classList.remove('hidden');
}

function closeLegalModal() {
  if (legalOverlay) legalOverlay.classList.add('hidden');
}

function checkDeepLinkBoot() {
  const bootData = decodeRecipeFromURL();
  if (!bootData) return;

  if (bootData.type === 'encoded' && bootData.recipe) {
    state.currentServings = bootData.servings;
    if (bootData.recipe.unitSystem) {
      setUnitSystem(bootData.recipe.unitSystem);
    }
    inputEl.value = bootData.recipe.mainIngredients.join(', ');
    clearBtn.classList.remove('hidden');
    state.lastGeneratedInput = inputEl.value.trim();
    state.seenTitles = [bootData.recipe.title];
    renderRecipe(bootData.recipe);
    showToast('Loaded shared recipe!');
  } else if (bootData.type === 'ingredients' && bootData.ingredientsText) {
    inputEl.value = bootData.ingredientsText;
    if (bootData.region && regionSelectEl) {
      regionSelectEl.value = bootData.region;
      state.selectedRegion = bootData.region;
    }
    if (bootData.unitSystem) {
      setUnitSystem(bootData.unitSystem);
    }
    state.currentServings = bootData.servings;
    clearBtn.classList.remove('hidden');
    handleGenerate();
  }
}

async function handleGenerate() {
  const parsed = parseIngredients(inputEl.value.trim());
  showLoading(true);

  try {
    const recipe = await generateRecipe(
      parsed.mainIngredients,
      parsed.utilityIngredients,
      state.selectedRegion,
      state.currentServings,
      state.seenTitles,
      state.unitSystem
    );

    if (recipe) {
      state.lastGeneratedInput = inputEl.value.trim();
      if (recipe.title && !state.seenTitles.includes(recipe.title)) {
        state.seenTitles.push(recipe.title);
      }
      renderRecipe(recipe);
    }
  } catch (err) {
    console.error('Error generating recipe', err);
    showToast('Failed to generate recipe. Please try again.');
  } finally {
    showLoading(false);
  }
}

async function handleRandom() {
  showLoading(true);
  state.seenTitles = [];

  try {
    const recipe = await getRandomRecipe(
      state.selectedRegion,
      state.currentServings,
      state.unitSystem
    );

    if (recipe) {
      inputEl.value = recipe.mainIngredients.join(', ');
      clearBtn.classList.remove('hidden');
      state.lastGeneratedInput = inputEl.value.trim();
      state.seenTitles = [recipe.title];
      renderRecipe(recipe);
    }
  } catch (err) {
    console.error('Error getting random recipe', err);
  } finally {
    showLoading(false);
  }
}

function showLoading(loading) {
  state.isLoading = loading;
  if (loading) {
    const randomPhrase = COOKING_PHRASES[Math.floor(Math.random() * COOKING_PHRASES.length)];
    if (loadingPhraseEl) loadingPhraseEl.textContent = randomPhrase;

    recipeLoadingState.classList.remove('hidden');
    recipeDisplay.classList.add('hidden');
  } else {
    recipeLoadingState.classList.add('hidden');
  }
}

function renderRecipe(recipe) {
  state.currentRecipe = recipe;
  
  // Update URL bar with shareable recipe state
  encodeRecipeToURL(recipe, state.currentServings);

  // Show recipe display & update button text to "Try Again"
  recipeDisplay.classList.remove('hidden');
  updateGenerateButtonText();

  recipeTitle.textContent = recipe.title;
  recipeTagline.textContent = recipe.tagline;

  const regionLabel = formatRegion(recipe.region);

  recipeMetaPills.innerHTML = `
    <span class="meta-pill">Servings: ${state.currentServings}</span>
    <span class="meta-pill">Prep: ${recipe.prepTime}</span>
    <span class="meta-pill">Cook: ${recipe.cookTime}</span>
    <span class="meta-pill">${regionLabel}</span>
  `;

  renderIngredientsList();

  // Steps
  stepsList.innerHTML = '';
  (recipe.steps || []).forEach((step, idx) => {
    const li = document.createElement('li');
    li.className = 'step-card';
    li.innerHTML = `
      <span class="step-num">${idx + 1}</span>
      <p class="step-text">${step}</p>
    `;
    stepsList.appendChild(li);
  });

  // Chef Tip
  if (recipe.chefTip) {
    chefTipBox.classList.remove('hidden');
    chefTipText.textContent = recipe.chefTip;
  } else {
    chefTipBox.classList.add('hidden');
  }

  updateServingsUI();

  if (window.innerWidth < 700) {
    recipeDisplay.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function renderIngredientsList() {
  if (!state.currentRecipe) return;

  const r = state.currentRecipe;
  const s = state.currentServings;
  const baseS = r.servings || 2;

  ingredientsList.innerHTML = '';

  // 1. Render Main Ingredients first (3 max)
  r.mainIngredients.forEach(ing => {
    const lowerIng = ing.toLowerCase();
    let qtyObj = r.baseQuantities ? r.baseQuantities[lowerIng] : null;
    
    if (!qtyObj && r.baseQuantities) {
      const matchKey = Object.keys(r.baseQuantities).find(k => k.includes(lowerIng) || lowerIng.includes(k));
      if (matchKey) qtyObj = r.baseQuantities[matchKey];
    }

    const scaledQty = qtyObj ? scaleQuantity(qtyObj.qty, baseS, s) : scaleQuantity(1, baseS, s);
    const unit = (qtyObj && qtyObj.unit) ? qtyObj.unit : '';

    const li = document.createElement('li');
    li.className = 'ingredient-item';
    li.innerHTML = `
      <span class="item-name">${capitalize(ing)}</span>
      <span class="item-qty">${scaledQty} ${unit}</span>
    `;
    ingredientsList.appendChild(li);
  });

  // 2. Render Pantry Staples directly below with zero dividers or distinct styling
  (r.utilityIngredients || []).forEach(u => {
    const scaledQty = scaleQuantity(u.qty, baseS, s);
    const li = document.createElement('li');
    li.className = 'ingredient-item';
    li.innerHTML = `
      <span class="item-name">${capitalize(u.name)}</span>
      <span class="item-qty">${scaledQty} ${u.unit || ''}</span>
    `;
    ingredientsList.appendChild(li);
  });
}

function updateServingsUI() {
  portionCountEl.textContent = state.currentServings;
  renderIngredientsList();
  
  if (state.currentRecipe) {
    encodeRecipeToURL(state.currentRecipe, state.currentServings);
    injectRecipeSchema(state.currentRecipe, state.currentServings);
  }
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatRegion(region) {
  const map = {
    'north-america': 'North American',
    'mediterranean': 'Mediterranean',
    'east-asia': 'East Asian',
    'latin-america': 'Latin American',
    'south-asia': 'South Asian',
    'europe': 'European',
    'all': 'Global',
    'global': 'Global'
  };
  return map[region] || capitalize(region);
}

function showToast(msg) {
  const existing = document.querySelector('.toast-msg');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'toast-msg';
  toast.textContent = msg;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 0.3s ease';
    setTimeout(() => toast.remove(), 300);
  }, 2000);
}
